import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { BarChart3, RefreshCw } from "lucide-react";
import { getBenefitUtilization } from "../../../../../Services/adminBenefits";
import { Button } from "../../../../../ui/button";
import { Card, CardContent } from "../../../../../ui/card";
import { Skeleton } from "../../../../../ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../ui/select";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../../../../ui/table";
import EmptyState from "../../../../../Components/admin/shared/EmptyState";
import { extractApiError, formatPercent } from "./utils";

const RANGE_OPTIONS = [
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
];

const toIsoDaysAgo = (days) => {
  const now = new Date();
  const start = new Date(now.getTime() - Number(days) * 24 * 60 * 60 * 1000);
  return { since: start.toISOString(), until: now.toISOString() };
};

const UtilizationSection = ({ benefitsEnabled }) => {
  const { id: corporateId } = useParams();
  const [range, setRange] = useState("30");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    if (!corporateId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const { since, until } = toIsoDaysAgo(range);
      const result = await getBenefitUtilization(corporateId, { since, until });
      setData(result);
    } catch (err) {
      setLoadError(extractApiError(err, "Failed to load utilization."));
    } finally {
      setLoading(false);
    }
  }, [corporateId, range]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = data?.rows || [];
  const maxSurfaced = useMemo(
    () => rows.reduce((m, r) => Math.max(m, r.surfaced_count || 0), 0),
    [rows]
  );

  if (!benefitsEnabled) {
    return (
      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold">Utilization</h2>
          <p className="text-sm text-muted-foreground">
            Aggregate counts only — no per-member data. Enable benefits in the
            header to start collecting offer history.
          </p>
        </div>
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Benefits are currently disabled — no utilization to show.
          </CardContent>
        </Card>
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Utilization</h2>
          <p className="text-sm text-muted-foreground">
            Aggregate offer counts per benefit. Privacy posture: aggregate only
            — never per-member.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={load} aria-label="Refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {loadError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {loadError}
        </div>
      )}

      {loading ? (
        <Card>
          <CardContent className="pt-4 space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </CardContent>
        </Card>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<BarChart3 className="h-8 w-8" />}
          title="No data yet"
          description="Utilization is built up as ARIS surfaces benefits during conversations. Expect a few days of lag after activation before counts appear."
        />
      ) : (
        <Card>
          <CardContent className="pt-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Benefit</TableHead>
                  <TableHead className="text-right">Surfaced</TableHead>
                  <TableHead className="text-right">Accepted</TableHead>
                  <TableHead className="text-right">Declined</TableHead>
                  <TableHead className="text-right">Ignored</TableHead>
                  <TableHead className="text-right">Delivered</TableHead>
                  <TableHead className="text-right">Accept rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const widthPct = maxSurfaced
                    ? Math.round(((row.surfaced_count || 0) / maxSurfaced) * 100)
                    : 0;
                  return (
                    <TableRow key={row.benefit_id}>
                      <TableCell className="min-w-[220px]">
                        <div className="text-sm font-medium">{row.name}</div>
                        <div
                          className="mt-1 h-1.5 rounded-full bg-muted overflow-hidden"
                          aria-hidden
                        >
                          <div
                            className="h-full bg-primary/70"
                            style={{ width: `${widthPct}%` }}
                          />
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {row.surfaced_count ?? 0}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {row.accepted_count ?? 0}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {row.declined_count ?? 0}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {row.ignored_count ?? 0}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {row.delivered_count ?? 0}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-medium">
                        {formatPercent(row.accept_rate)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <p className="text-xs text-muted-foreground mt-3">
              Total offers in range: {data?.total_offers ?? 0}
            </p>
          </CardContent>
        </Card>
      )}
    </section>
  );
};

export default UtilizationSection;
